<template>
  <div class="layout-root">
    <aside class="layout-sidebar">
      <div class="sidebar-brand">
        <div class="brand-logo">
          <i class="el-icon-office-building"></i>
        </div>
        <div class="brand-copy">
          <div class="brand-name">{{ $t('app.name') }}</div>
          <div class="brand-sub">{{ $t('common.workbench') }}</div>
        </div>
      </div>

      <nav class="sidebar-nav">
        <section
          v-for="group in groupedMenu"
          :key="group.key"
          class="menu-section"
        >
          <div class="menu-section__title">{{ $t('navGroups.' + group.key) }}</div>

          <template v-for="item in group.items">
            <div
              v-if="item.children && item.children.length"
              :key="'g-' + item.id"
              class="nav-group"
            >
              <div
                class="nav-link nav-group-title"
                :class="{ active: isGroupActive(item) }"
                @click="toggleGroup(item.id)"
              >
                <i :class="item.icon"></i>
                <span>{{ item.label }}</span>
                <em v-if="badgeValue(item)" class="nav-badge">{{ badgeValue(item) }}</em>
                <i
                  class="nav-arrow"
                  :class="expandedGroups.indexOf(item.id) >= 0 ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"
                ></i>
              </div>
              <div v-show="expandedGroups.indexOf(item.id) >= 0" class="nav-sub">
                <router-link
                  v-for="child in item.children"
                  :key="child.id"
                  :to="child.routePath"
                  class="nav-sub-link"
                  :class="{ active: isRouteActive(child.routePath) }"
                >
                  <span>{{ child.label }}</span>
                  <em v-if="badgeValue(child)" class="nav-badge nav-badge--sub">{{ badgeValue(child) }}</em>
                </router-link>
              </div>
            </div>

            <router-link
              v-else
              :key="'l-' + item.id"
              :to="item.routePath"
              class="nav-link"
              :class="{ active: isRouteActive(item.routePath) }"
            >
              <i :class="item.icon"></i>
              <span>{{ item.label }}</span>
              <em v-if="badgeValue(item)" class="nav-badge">{{ badgeValue(item) }}</em>
            </router-link>
          </template>
        </section>
      </nav>

      <div class="sidebar-footer">
        <div class="sidebar-preferences">
          <div class="locale-switcher">
            <span class="locale-switcher__label">{{ $t('common.language') }}</span>
            <el-select
              :value="$locale"
              size="mini"
              class="locale-switcher__select"
              popper-class="layout-dark-popper"
              @input="handleLocaleChange"
            >
              <el-option
                v-for="option in $languageOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>

          <div class="theme-switcher">
            <span class="theme-switcher__label">{{ $t('common.appearance') }}</span>
            <el-select
              :value="themeMode"
              size="mini"
              class="theme-switcher__select"
              popper-class="layout-dark-popper"
              @input="handleThemeChange"
            >
              <el-option :label="$t('common.lightMode')" value="light" />
              <el-option :label="$t('common.darkMode')" value="dark" />
            </el-select>
          </div>
        </div>

        <div class="sidebar-user-wrap">
          <el-dropdown trigger="click" @command="handleCommand" placement="top-start">
            <div class="user-btn">
              <div class="user-avatar">{{ displayNameInitial }}</div>
              <div class="user-info">
                <div class="user-display-name">{{ displayName }}</div>
                <div v-if="roleLabel" class="user-role">{{ roleLabel }}</div>
              </div>
              <i class="el-icon-more"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item disabled class="dropdown-meta">
                <div>{{ $t('common.account') }}: {{ username || '—' }}</div>
                <div v-if="roleLabel">{{ $t('common.role') }}: {{ roleLabel }}</div>
              </el-dropdown-item>
              <el-dropdown-item icon="el-icon-switch-button" divided command="logout">{{ $t('common.logout') }}</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </div>
    </aside>

    <div class="layout-content" :class="{ 'layout-content--record': compactWorkspaceHeader }">
      <header v-if="!compactWorkspaceHeader" class="workspace-header">
        <div class="workspace-header__copy">
          <h1 class="workspace-header__title">{{ pageTitle }}</h1>
          <p class="workspace-header__subtitle">{{ pageSubtitle }}</p>
        </div>
        <div class="workspace-header__summary">
          <div class="summary-chip">
            <span class="summary-chip__label">{{ $t('dashboardPage.tasksTitle') }}</span>
            <strong class="summary-chip__value">{{ taskCount }}</strong>
          </div>
          <div class="summary-chip">
            <span class="summary-chip__label">{{ $t('schedulePage.upcoming') }}</span>
            <strong class="summary-chip__value">{{ visitCount }}</strong>
          </div>
        </div>
      </header>

      <main class="layout-main" :class="{ 'layout-main--record': compactWorkspaceHeader }">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import { NAV_GROUP_ORDER, normalizeRoute, routeGroup, routeIcon, routeTitleKey } from '@/config/navigation'

const ROUTE_REDIRECT_MAP = {
  '/dashboard': '/home',
  '/leads': '/lead/list',
  '/customers': '/customer/list',
  '/opportunities': '/lead/list',
  '/quotes': '/quote/list',
  '/contracts': '/contract/list',
  '/projects': '/project/list',
  '/subcontractors': '/subcontractor/list',
  '/reports': '/report/dashboard',
  '/invoices': '/finance/overview',
  '/system': '/system/overview'
}

const PAGE_SUBTITLE_PREFIXES = [
  { prefix: '/system/contract-templates/new', key: 'workspaceHints.contractTemplateForm' },
  { prefix: '/system/contract-templates/edit', key: 'workspaceHints.contractTemplateForm' },
  { prefix: '/system/contract-templates', key: 'pipelineList.contractTemplates' },
  { prefix: '/system/users', key: 'settingsPageExtra.usersSubtitle' },
  { prefix: '/system/roles', key: 'settingsPageExtra.rolesSubtitle' },
  { prefix: '/system/menus', key: 'settingsPageExtra.menusSubtitle' },
  { prefix: '/system/mail-settings', key: 'emailConfigPage.subtitle' },
  { prefix: '/system/overview', key: 'settingsPage.subtitle' },
  { prefix: '/lead/list', key: 'pipelineList.leads' },
  { prefix: '/lead/new', key: 'workspaceHints.leadNew' },
  { prefix: '/lead/detail', key: 'workspaceHints.leadDetail' },
  { prefix: '/customer/list', key: 'pipelineList.customers' },
  { prefix: '/customer/new', key: 'workspaceHints.customerNew' },
  { prefix: '/customer/edit', key: 'workspaceHints.customerEdit' },
  { prefix: '/quote/list', key: 'pipelineList.quotes' },
  { prefix: '/quote/new', key: 'workspaceHints.quoteNew' },
  { prefix: '/quote/edit', key: 'workspaceHints.quoteEdit' },
  { prefix: '/contract/list', key: 'pipelineList.contracts' },
  { prefix: '/contract/from-quote', key: 'workspaceHints.contractFromQuote' },
  { prefix: '/contract/detail', key: 'workspaceHints.contractDetail' },
  { prefix: '/change-order/list', key: 'pipelineList.changeOrders' },
  { prefix: '/project/list', key: 'pipelineList.projects' },
  { prefix: '/project/detail', key: 'workspaceHints.projectDetail' },
  { prefix: '/subcontractor/list', key: 'pipelineList.subcontractors' },
  { prefix: '/subcontractor/new', key: 'workspaceHints.subcontractorNew' },
  { prefix: '/subcontractor/edit', key: 'workspaceHints.subcontractorEdit' },
  { prefix: '/finance/lead-cost', key: 'workspaceHints.leadCostMonth' },
  { prefix: '/finance/overview', key: 'workspaceHints.financeOverview' },
  { prefix: '/sales-amount/list', key: 'workspaceHints.salesAmount' },
  { prefix: '/report/dashboard', key: 'reportPage.subtitle' },
  { prefix: '/schedule/calendar', key: 'schedulePage.subtitle' },
  { prefix: '/home', key: 'dashboardPage.subtitle' },
  { prefix: '/reports', key: 'reportPage.subtitle' },
  { prefix: '/report', key: 'reportPage.subtitle' },
  { prefix: '/schedule', key: 'schedulePage.subtitle' },
  { prefix: '/finance', key: 'workspaceHints.financeOverview' },
  { prefix: '/sales-amount', key: 'workspaceHints.salesAmount' },
  { prefix: '/project', key: 'projectPage.subtitle' },
  { prefix: '/system', key: 'settingsPage.subtitle' }
]

const PAGE_SUBTITLE_PREFIXES_SORTED = [...PAGE_SUBTITLE_PREFIXES].sort(
  (a, b) => b.prefix.length - a.prefix.length
)

function deepClone(nodes) {
  return (nodes || []).map(item => ({
    ...item,
    children: deepClone(item.children || [])
  }))
}

export default {
  name: 'Layout',
  data() {
    return {
      expandedGroups: []
    }
  },
  computed: {
    baseMenuTree() {
      const tree = deepClone(store.state.menuTree || [])
      const routePaths = []
      const walk = nodes => {
        nodes.forEach(node => {
          routePaths.push(node.routePath)
          walk(node.children || [])
        })
      }
      walk(tree)
      if (!routePaths.some(p => String(p || '').includes('/dashboard'))) {
        tree.unshift({
          id: 'menu-dashboard',
          menuName: 'Dashboard',
          routePath: '/dashboard',
          children: []
        })
      }
      if (!routePaths.some(p => String(p || '').includes('/schedule'))) {
        tree.push({
          id: 'menu-schedule',
          menuName: 'Schedule',
          routePath: '/schedule/calendar',
          children: []
        })
      }
      return tree
    },
    groupedMenu() {
      const buckets = NAV_GROUP_ORDER.reduce((acc, key) => {
        acc[key] = []
        return acc
      }, {})

      this.baseMenuTree.forEach(item => {
        const normalized = this.decorateMenuItem(item)
        buckets[normalized.group] = buckets[normalized.group] || []
        buckets[normalized.group].push(normalized)
      })

      // 不再自动插入「系统总览」：此前未配 i18nKey 时会被 routeTitleKey 误标成 nav.settings（设置中心），
      // 且相当于绕过菜单管理。需要总览请在 sys_menu 中为角色分配对应菜单（i18n_key: nav.systemOverview）。
      return NAV_GROUP_ORDER
        .map(key => ({ key, items: buckets[key] || [] }))
        .filter(group => group.items.length)
    },
    username() {
      const u = store.state.userInfo
      return (u && u.username) || ''
    },
    displayName() {
      const u = store.state.userInfo
      if (!u) return 'User'
      return (u.realName && u.realName.trim()) || u.username || 'User'
    },
    displayNameInitial() {
      return (this.displayName || '?').charAt(0).toUpperCase()
    },
    roleLabel() {
      const roles = (store.state.userInfo && store.state.userInfo.roleCodes) || []
      return roles.length ? roles.join(' · ') : ''
    },
    pageTitle() {
      const matched = this.$route.matched
      const leaf = matched.length ? matched[matched.length - 1] : null
      const meta = leaf && leaf.meta
      if (meta && meta.titleKey) return this.$t(meta.titleKey)
      const titleKey = routeTitleKey(this.$route.path)
      if (titleKey) return this.$t(titleKey)
      if (meta && meta.title) return meta.title
      return this.$t('app.name')
    },
    pageSubtitle() {
      const path = this.$route.path
      const matched = this.$route.matched
      const leaf = matched.length ? matched[matched.length - 1] : null
      const meta = leaf && leaf.meta
      if (meta && meta.subtitleKey) return this.$t(meta.subtitleKey)
      const hit = PAGE_SUBTITLE_PREFIXES_SORTED.find(item => path.startsWith(item.prefix))
      if (hit) return this.$t(hit.key)
      return this.$t('app.tagline')
    },
    taskCount() {
      const tasks = (store.state.workbench && store.state.workbench.tasks) || []
      return tasks.length
    },
    visitCount() {
      const visits = (store.state.workbench && store.state.workbench.upcomingVisits) || []
      return visits.length
    },
    /** Record (detail / form) pages provide their own title row — hide duplicate workspace header */
    compactWorkspaceHeader() {
      return this.$route.matched.some(r => r.meta && r.meta.compactWorkspaceHeader)
    },
    themeMode() {
      return this.$store.state.themeMode
    }
  },
  watch: {
    $route(to) {
      this.autoExpandGroup(to.path)
    }
  },
  created() {
    this.autoExpandGroup(this.$route.path)
  },
  methods: {
    decorateMenuItem(item) {
      const routePath = ROUTE_REDIRECT_MAP[item.routePath] || item.routePath
      const children = (item.children || []).map(child => this.decorateMenuItem(child))
      const titleKey = item.i18nKey || routeTitleKey(routePath)
      return {
        ...item,
        routePath,
        icon: item.iconKey || routeIcon(routePath, 'el-icon-menu'),
        label: titleKey ? this.$t(titleKey) : item.menuName,
        group: item.menuGroup || routeGroup(routePath),
        children
      }
    },
    badgeValue(item) {
      const badges = (store.state.workbench && store.state.workbench.badges) || {}
      const route = normalizeRoute(item.routePath)
      if (route.startsWith('/quote') || route.startsWith('/quotes')) return badges.pendingQuotes || 0
      if (route.startsWith('/contract') || route.startsWith('/contracts')) return badges.awaitingContracts || 0
      if (route.startsWith('/schedule')) return badges.upcomingVisits || 0
      if (route.startsWith('/finance') || route.startsWith('/invoices')) return badges.pendingInvoices || 0
      return 0
    },
    isRouteActive(routePath) {
      return normalizeRoute(this.$route.path) === normalizeRoute(routePath)
        || normalizeRoute(this.$route.path).startsWith(normalizeRoute(routePath) + '/')
    },
    isGroupActive(item) {
      return item.children && item.children.some(child => this.isRouteActive(child.routePath))
    },
    toggleGroup(id) {
      const idx = this.expandedGroups.indexOf(id)
      if (idx >= 0) {
        this.expandedGroups.splice(idx, 1)
      } else {
        this.expandedGroups.push(id)
      }
    },
    autoExpandGroup(path) {
      this.groupedMenu.forEach(group => {
        group.items.forEach(item => {
          if (item.children && item.children.length && item.children.some(child => this.isRouteActive(child.routePath) || String(path).startsWith(child.routePath))) {
            if (this.expandedGroups.indexOf(item.id) < 0) this.expandedGroups.push(item.id)
          }
        })
      })
    },
    handleLocaleChange(locale) {
      this.$store.dispatch('switchLocale', locale)
    },
    handleThemeChange(mode) {
      this.$store.dispatch('switchThemeMode', mode)
    },
    handleCommand(cmd) {
      if (cmd === 'logout') {
        this.$confirm(this.$t('common.logoutConfirm'), this.$t('common.logout'), {
          confirmButtonText: this.$t('common.logoutAction'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
          .then(() => {
            this.$store.dispatch('logout')
            this.$router.replace('/login')
          })
          .catch(() => {})
      }
    }
  }
}
</script>

<style scoped>
.layout-root {
  /* 与侧栏「工作台」品牌条同高，保证顶栏底线对齐 */
  --layout-workbench-bar-height: 83px;
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: var(--color-bg);
}

.layout-sidebar {
  width: 246px;
  min-width: 246px;
  background: var(--layout-sidebar-bg);
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--layout-sidebar-border);
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  height: var(--layout-workbench-bar-height);
  min-height: var(--layout-workbench-bar-height);
  padding: 0 16px;
  border-bottom: 1px solid var(--layout-sidebar-border);
}

.brand-logo {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(145deg, #86efac 0%, #4ade80 42%, #15803d 100%);
  color: #0c0e14;
  font-size: 18px;
  box-shadow:
    0 8px 24px rgba(74, 222, 128, 0.32),
    0 0 0 1px rgba(255, 255, 255, 0.12) inset;
}

.brand-copy {
  min-width: 0;
}

.brand-name {
  color: var(--color-text);
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
}

.brand-sub {
  margin-top: 4px;
  color: var(--color-text-sub);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 12px 8px;
}

.menu-section + .menu-section {
  margin-top: 14px;
}

.menu-section__title {
  padding: 0 12px 8px;
  color: var(--color-text-sub);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 2px;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  color: var(--color-text-sub);
  font-size: 13px;
  font-weight: 500;
  transition: background 0.18s ease, color 0.18s ease;
}

.nav-link:hover {
  background: var(--layout-hover-bg);
  color: var(--color-text);
}

.nav-link:hover i:first-child {
  color: var(--color-text);
}

.nav-link.active {
  background: var(--color-brand-soft);
  color: var(--color-brand);
  font-weight: 600;
  box-shadow: 0 0 20px rgba(74, 222, 128, 0.08);
}

.nav-link.active i:first-child {
  color: var(--color-brand);
  filter: drop-shadow(0 0 5px rgba(74, 222, 128, 0.35));
}

.nav-link i:first-child {
  width: 18px;
  text-align: center;
  flex-shrink: 0;
  color: var(--color-text-sub);
  transition: color 0.18s ease, filter 0.18s ease;
}

.nav-link span {
  flex: 1;
  min-width: 0;
}

.nav-group-title {
  cursor: pointer;
}

.nav-arrow {
  font-size: 11px;
  color: var(--color-text-sub);
  opacity: 0.95;
}

.nav-link:hover .nav-arrow {
  color: var(--color-text);
}

.nav-link.active .nav-arrow {
  color: var(--color-brand);
}

.nav-sub {
  padding-left: 12px;
  margin-bottom: 4px;
}

.nav-sub-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  color: var(--color-text-sub);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  text-decoration: none;
  border-left: 2px solid transparent;
  border-radius: 8px;
}

.nav-sub-link:hover {
  background: var(--layout-hover-bg);
  color: var(--color-text);
}

.nav-sub-link.active {
  border-left-color: var(--color-brand);
  color: var(--color-brand);
  background: var(--color-brand-soft);
  font-weight: 600;
  box-shadow: inset 0 0 12px rgba(74, 222, 128, 0.06);
}

.nav-badge {
  min-width: 18px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(74, 222, 128, 0.22);
  color: #6ee7a7;
  box-shadow: 0 0 0 1px rgba(74, 222, 128, 0.2);
  font-style: normal;
  font-size: 11px;
  text-align: center;
}

.nav-badge--sub {
  margin-left: auto;
}

.sidebar-footer {
  padding: 12px 0 0;
  border-top: 1px solid var(--layout-sidebar-border);
}

.sidebar-user-wrap {
  width: 100%;
}

.sidebar-user-wrap :deep(.el-dropdown) {
  display: block;
  width: 100%;
}

.sidebar-preferences {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  margin: 0 8px 10px;
  padding: 8px 10px;
  border-radius: 10px;
  background: var(--layout-panel-bg);
  align-items: end;
}

.locale-switcher,
.theme-switcher {
  min-width: 0;
}

.locale-switcher__label,
.theme-switcher__label {
  display: block;
  margin-bottom: 6px;
  color: var(--color-text-sub);
  font-size: 11px;
}

.locale-switcher__select,
.theme-switcher__select {
  width: 100%;
}

.user-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px 16px;
  border-radius: 0;
  cursor: pointer;
}

.user-btn:hover {
  background: var(--layout-hover-bg);
}

.user-btn .el-icon-more {
  color: var(--color-text-sub);
  font-size: 14px;
}

.user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #0c0e14;
  background: linear-gradient(145deg, #86efac 0%, #4ade80 45%, #15803d 100%);
  box-shadow:
    0 4px 14px rgba(74, 222, 128, 0.28),
    0 0 0 1px rgba(255, 255, 255, 0.1) inset;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-display-name {
  color: var(--color-text);
  font-size: 13px;
  font-weight: 600;
}

.user-role {
  margin-top: 3px;
  color: var(--color-text-sub);
  font-size: 11px;
}

.layout-content {
  flex: 1;
  min-width: 0;
  overflow: auto;
  background: var(--color-bg);
}

.workspace-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  box-sizing: border-box;
  height: var(--layout-workbench-bar-height);
  min-height: var(--layout-workbench-bar-height);
  max-height: var(--layout-workbench-bar-height);
  padding: 0 24px;
  background: var(--layout-header-bg);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--color-border-soft);
}

.workspace-header__copy {
  min-width: 0;
  flex: 1;
}

.workspace-header__title {
  margin: 0;
  color: var(--color-text);
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
}

.workspace-header__subtitle {
  margin: 2px 0 0;
  color: var(--color-text-sub);
  font-size: 11px;
  line-height: 1.3;
  max-width: 720px;
  letter-spacing: 0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.workspace-header__summary {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  flex-shrink: 0;
  align-items: center;
}

.summary-chip {
  min-width: 0;
  padding: 5px 10px;
  border-radius: 8px;
  background: var(--dark-surface);
  border: 1px solid var(--dark-border);
}

.summary-chip__label {
  display: block;
  color: var(--dark-text-sub);
  font-size: 9px;
  margin-bottom: 1px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  line-height: 1.15;
  white-space: nowrap;
}

.summary-chip__value {
  color: var(--dark-text);
  font-size: 16px;
  line-height: 1.1;
}

.layout-main {
  min-height: calc(100vh - var(--layout-workbench-bar-height));
}

.layout-main--record {
  min-height: calc(100vh - 56px);
}

@media (max-width: 1200px) {
  .layout-sidebar {
    width: 220px;
    min-width: 220px;
  }
}

@media (max-width: 900px) {
  .workspace-header {
    height: auto;
    min-height: var(--layout-workbench-bar-height);
    max-height: none;
    padding: 10px 16px;
    flex-direction: column;
    align-items: stretch;
  }

  .workspace-header__subtitle {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .workspace-header__summary {
    flex-wrap: wrap;
  }
}
</style>

<style>
.dropdown-meta {
  line-height: 1.7 !important;
  font-size: 12px !important;
  color: #909399 !important;
  cursor: default !important;
}

.layout-dark-popper .el-select-dropdown__item {
  color: #cbd5e1 !important;
}
</style>
